import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import OnlineBadge from '../components/OnlineBadge';
import AbnormalReview from '../components/AbnormalReview';
import MediaField from '../components/MediaField';
import { cachedTag, invalidateTag, useTagResolve } from './useTagResolve';
import type { AbnormalReport, TagResolveOk, TemplateField } from '../api/types';
import { useSession } from '../auth/session';
import { submitLocalFirst } from '../sync/submit';
import { loadSnapshot } from '../db/idb';
import { snapshotSync, noteLocalReading } from '../db/snapshot';
import { capturedNowIso } from '../lib/serverTime';
import { evaluateThresholds } from '../lib/thresholds';

export default function ReadingForm() {
  const { code = '' } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const session = useSession();

  const cached = cachedTag(code);
  const resolved = useTagResolve(code, sp.get('k'));
  const data: TagResolveOk | undefined = cached ?? resolved.data;

  const submissionId = useRef<string>(crypto.randomUUID());
  const [step, setStep] = useState(0); // 0..sections-1 = section, sections = review
  const [values, setValues] = useState<Record<string, string>>({});
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [abnormal, setAbnormal] = useState<AbnormalReport | null>(null);
  const [thresholdRows, setThresholdRows] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    loadSnapshot().then((s) => s && setThresholdRows(s.thresholds)).catch(() => {});
  }, []);

  const sections = data?.template.sections ?? [];
  const lastValues = data?.lastValues ?? {};
  const allFields = useMemo(() => sections.flatMap((s) => s.fields), [sections]);
  const reviewStep = sections.length;

  if (!session) return <Navigate to="/signin" state={{ from: `/t/${code}/form` }} replace />;
  if (resolved.loading && !data) return <div className="app"><div className="spinner" /></div>;
  if (!data) {
    return (
      <div className="app pad center">
        <p className="error">{resolved.error ?? 'Could not load this equipment.'}</p>
        <button className="secondary" onClick={() => nav('/')}>Back</button>
      </div>
    );
  }

  function setVal(key: string, v: string) {
    setValues((p) => ({ ...p, [key]: v }));
    setErrors((p) => (p[key] ? { ...p, [key]: '' } : p));
  }

  const isNumeric = (t: TemplateField['type']) => t === 'decimal' || t === 'int';
  const isMedia = (t: TemplateField['type']) => t === 'photo' || t === 'video';

  function validateFields(fields: TemplateField[]): Record<string, string> {
    const errs: Record<string, string> = {};
    for (const f of fields) {
      const raw = (values[f.key] ?? '').trim();
      if (!raw) {
        if (f.required !== false) errs[f.key] = 'Required';
        continue;
      }
      if (isNumeric(f.type) && Number.isNaN(Number(raw))) errs[f.key] = 'Numbers only';
    }
    return errs;
  }

  function numericValues(): Record<string, number | string> {
    const out: Record<string, number | string> = {};
    for (const f of allFields) {
      const raw = (values[f.key] ?? '').trim();
      if (!raw) continue;
      out[f.key] = isNumeric(f.type) ? Number(raw) : raw;
    }
    return out;
  }

  function next() {
    setFormError('');
    const errs = validateFields(sections[step].fields);
    setErrors(errs);
    if (Object.keys(errs).length) { setFormError('Check the highlighted fields.'); return; }
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  }

  async function doSubmit(confirmedAbnormal: boolean) {
    setBusy(true); setFormError('');
    try {
      const errs = validateFields(allFields);
      if (Object.keys(errs).length) {
        setErrors(errs);
        setFormError('Some readings are missing. Go back and complete them.');
        setBusy(false);
        return;
      }

      // The server runs these same three checks; we run them here so the operator
      // gets the answer instantly instead of after a round trip.
      if (data!.schedule.canSubmit === false) {
        setStep(reviewStep);
        setFormError(`${data!.schedule.reason} This reading cannot be submitted yet.`);
        setAbnormal(null);
        setBusy(false);
        return;
      }
      if (!confirmedAbnormal && thresholdRows.length) {
        const rep = evaluateThresholds(thresholdRows, data!.equipment.equipmentId, data!.template.templateId, numericValues());
        if (rep.status !== 'normal') { setAbnormal(rep); setBusy(false); return; }
      }

      const item = await submitLocalFirst({
        tagCode: data!.tag.code,
        equipmentName: data!.equipment.name,
        location: data!.equipment.location,
        values: numericValues(),
        remarks,
        confirmedAbnormal,
      }, submissionId.current);
      // next scan re-checks the schedule against this new reading
      invalidateTag(code);
      noteLocalReading(data!.equipment.equipmentId, numericValues());

      const offline = navigator.onLine === false;
      const target = snapshotSync()?.config.targetIntervalMin ?? 120;
      nav('/success', { replace: true, state: {
        reading: {
          equipmentName: data!.equipment.name, location: data!.equipment.location,
          operatorName: session!.operatorName, effectiveReadingAt: item.clientCapturedAt,
          abnormalStatus: confirmedAbnormal ? 'warning' : 'normal',
          source: offline ? 'offline' : 'online',
        },
        nextRecommendedAt: new Date(Date.parse(capturedNowIso()) + target * 60_000).toISOString(),
        queued: true,
        syncing: !offline,
      } });
    } finally {
      setBusy(false);
    }
  }

  const onReview = step >= reviewStep;
  const progress = Array.from({ length: sections.length + 1 });

  return (
    <div className="app">
      <OnlineBadge />
      <div className="wiz-top">
        <button className="back" onClick={() => (step === 0 ? nav('/') : setStep((s) => s - 1))}>‹ Back</button>
        <div className="wiz-progress">
          {progress.map((_, i) => (
            <i key={i} className={i < step ? 'done' : i === step ? 'cur' : ''} />
          ))}
        </div>
      </div>

      <div className="wiz-title">{data.equipment.name}</div>

      {data.schedule.canSubmit === false && (
        <p className="error" style={{ marginTop: 8 }}>
          {data.schedule.reason} You can fill this in, but it can only be submitted after that.
        </p>
      )}

      {!onReview && (
        <>
          <div className="wiz-step-name">{sections[step].label}</div>
          <div className="wiz-title" style={{ margin: '0 0 10px' }}>Step {step + 1} of {sections.length}</div>
          {sections[step].fields.map((f) => (
            <div className="field-lg" key={f.key}>
              <label htmlFor={f.key}>
                <span>{f.label}</span>
                {!isMedia(f.type) && lastValues[f.key] !== undefined && <span className="was">last: {String(lastValues[f.key])}</span>}
              </label>

              {f.type === 'select' ? (
                <div className="choice-group" role="radiogroup" aria-label={f.label}>
                  {(f.options ?? []).map((opt) => (
                    <button type="button" key={opt} className={`choice-btn ${values[f.key] === opt ? 'on' : ''}`}
                      aria-pressed={values[f.key] === opt} onClick={() => setVal(f.key, opt)}>
                      {opt}
                    </button>
                  ))}
                </div>
              ) : isMedia(f.type) ? (
                <MediaField field={f} value={values[f.key] ?? ''} equipmentId={data.equipment.equipmentId}
                  onChange={(url) => setVal(f.key, url)} />
              ) : (
                <div className="box">
                  <input
                    id={f.key}
                    inputMode={f.type === 'text' ? 'text' : 'decimal'}
                    enterKeyHint="next"
                    autoComplete="off"
                    placeholder={lastValues[f.key] !== undefined ? String(lastValues[f.key]) : ''}
                    value={values[f.key] ?? ''}
                    aria-invalid={!!errors[f.key]}
                    onChange={(e) => setVal(f.key, e.target.value)}
                  />
                  {f.unit && <span className="unit">{f.unit}</span>}
                </div>
              )}
              {errors[f.key] && <div className="err">{errors[f.key]}</div>}
            </div>
          ))}
        </>
      )}

      {onReview && (
        <>
          <div className="wiz-step-name">Check &amp; submit</div>
          {sections.map((s) => (
            <div key={s.key}>
              <div className="review-sec">{s.label}</div>
              {s.fields.map((f) => {
                const v = (values[f.key] ?? '').trim();
                const shown = isMedia(f.type) ? (v ? `✓ ${f.type === 'video' ? 'Video' : 'Photo'} attached` : '—') : (v || '—');
                return (
                  <div className="review-item" key={f.key}>
                    <span className="rk">{f.label}</span>
                    <span style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                      <span className="rv">{shown}{v && !isMedia(f.type) && f.unit ? ` ${f.unit}` : ''}</span>
                      <button onClick={() => { setStep(sections.indexOf(s)); window.scrollTo(0, 0); }}>edit</button>
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          <label style={{ marginTop: 16 }}>Observation / Remarks</label>
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional" />
        </>
      )}

      {formError && <p className="error" style={{ whiteSpace: 'pre-line', marginTop: 12 }}>{formError}</p>}

      <div className="wiz-nav">
        {!onReview
          ? <button onClick={next}>{step + 1 < sections.length ? 'Next' : 'Review'}</button>
          : <button onClick={() => void doSubmit(false)} disabled={busy || data.schedule.canSubmit === false}>
              {busy ? 'Saving…' : navigator.onLine === false ? 'Save reading (offline)' : 'Submit reading'}
            </button>}
      </div>

      {abnormal && (
        <AbnormalReview
          report={abnormal}
          template={data.template}
          busy={busy}
          onEdit={() => { setAbnormal(null); setStep(reviewStep); }}
          onConfirm={() => void doSubmit(true)}
        />
      )}
    </div>
  );
}
